import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useTestAlerting, {
  type TestAlertingKind,
} from "@babackapp/private-api-client/client/use-test-alerting";
import useUpdateShopPreferences from "@babackapp/private-api-client/client/use-update-shop-preferences";
import FormOptionsSelector from "@babackapp/react-hook-forms-components/form-option-selector";
import SettingsFormContainer, {
  SettingsFormHeader,
  SettingsFormPageButtonBar,
  SettingsFormSectionHeader,
} from "@babackapp/react-hook-forms-components/form-page";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { Card, CardContent } from "@babackapp/ui/components/card";
import {
  Form,
  FormDescription,
  FormField,
  FormLabel,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const AlertsSettingsSchema = z.object({
  isEnabled: z.boolean(),
  isBlockingReturnsEnabled: z.boolean(),
  isPendingActionsEnabled: z.boolean(),
  recipientEmails: z.array(z.string().email()),
  sendHour: z.number().int().min(0).max(23),
  isTechnicalAlertsEnabled: z.boolean(),
  technicalRecipientEmails: z.array(z.string().email()),
});

type AlertsSettingsFormValues = z.infer<typeof AlertsSettingsSchema>;

const SEND_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  key: i,
  label: `${String(i).padStart(2, "0")}:00`,
}));

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AlertsSettingsPage() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updatePreferences } = useUpdateShopPreferences(shop.id);
  const { mutateAsync: testAlerting, isPending: isTestPending } =
    useTestAlerting(shop.id);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState<{
    kind: TestAlertingKind;
    alertCount: number;
    sent: boolean;
  } | null>(null);

  const alerting = shop.preferences?.alerting;

  const form = useForm<AlertsSettingsFormValues>({
    resolver: zodResolver(AlertsSettingsSchema),
    defaultValues: {
      isEnabled: alerting?.isEnabled ?? false,
      isBlockingReturnsEnabled: alerting?.isBlockingReturnsEnabled ?? true,
      isPendingActionsEnabled: alerting?.isPendingActionsEnabled ?? true,
      recipientEmails: alerting?.recipientEmails ?? [],
      sendHour: alerting?.sendHour ?? 8,
      isTechnicalAlertsEnabled: alerting?.isTechnicalAlertsEnabled ?? true,
      technicalRecipientEmails: alerting?.technicalRecipientEmails ?? [],
    },
  });

  const onSubmit = async (values: AlertsSettingsFormValues) => {
    await updatePreferences({ alerting: values });
  };

  const onTestAlerting = async (kind: TestAlertingKind) => {
    setTestResult(null);
    const result = await testAlerting({ email: testEmail, kind });
    if (result) setTestResult({ kind, ...result });
  };

  return (
    <SettingsFormContainer>
      <SettingsFormHeader
        title={t("settings.alerts.title")}
        description={t("settings.alerts.description")}
      />
      <Card>
        <CardContent className="pt-6 space-y-8">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <SettingsFormSectionHeader
                title={t("settings.alerts.general")}
                id="general"
              />

              <FormField
                control={form.control}
                name="isEnabled"
                render={({ field }) => (
                  <FormSwitch
                    label={t("settings.alerts.isEnabled.label")}
                    description={t("settings.alerts.isEnabled.description")}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="isBlockingReturnsEnabled"
                render={({ field }) => (
                  <FormSwitch
                    label={t("settings.alerts.blockingReturns.label")}
                    description={t(
                      "settings.alerts.blockingReturns.description",
                    )}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="isPendingActionsEnabled"
                render={({ field }) => (
                  <FormSwitch
                    label={t("settings.alerts.pendingActions.label")}
                    description={t(
                      "settings.alerts.pendingActions.description",
                    )}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="recipientEmails"
                render={({ field }) => (
                  <RecipientEmailsInput
                    label={t("settings.alerts.recipients.label")}
                    description={t("settings.alerts.recipients.description")}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="sendHour"
                render={({ field }) => (
                  <FormOptionsSelector
                    label={t("settings.alerts.sendHour.label")}
                    description={t("settings.alerts.sendHour.description")}
                    value={field.value}
                    options={SEND_HOUR_OPTIONS}
                    onChange={(v) => field.onChange(Number(v))}
                  />
                )}
              />

              <SettingsFormSectionHeader
                title={t("settings.alerts.technical.title")}
                id="technical"
              />

              <FormField
                control={form.control}
                name="isTechnicalAlertsEnabled"
                render={({ field }) => (
                  <FormSwitch
                    label={t("settings.alerts.technical.enabled.label")}
                    description={t(
                      "settings.alerts.technical.enabled.description",
                    )}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="technicalRecipientEmails"
                render={({ field }) => (
                  <RecipientEmailsInput
                    label={t("settings.alerts.technical.recipients.label")}
                    description={t(
                      "settings.alerts.technical.recipients.description",
                    )}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <SettingsFormPageButtonBar />
            </form>
          </Form>

          <div className="border-t pt-8 space-y-4">
            <SettingsFormSectionHeader
              title={t("settings.alerts.test.title")}
              id="test"
            />
            <p className="text-sm text-muted-foreground">
              {t("settings.alerts.test.description")}
            </p>
            <div className="flex gap-3 items-center max-w-md">
              <Input
                type="email"
                placeholder={t("settings.alerts.test.emailPlaceholder")}
                value={testEmail}
                onChange={(e) => {
                  setTestEmail(e.target.value);
                  setTestResult(null);
                }}
              />
              <Button
                type="button"
                variant="outline"
                disabled={!testEmail || isTestPending}
                onClick={() => onTestAlerting("digest")}
              >
                {isTestPending
                  ? t("settings.alerts.test.sending")
                  : t("settings.alerts.test.button")}
              </Button>
            </div>
            <Button
              type="button"
              variant="link"
              className="px-0"
              disabled={!testEmail || isTestPending}
              onClick={() => onTestAlerting("technical")}
            >
              {t("settings.alerts.technical.test.button")}
            </Button>
            {testResult && (
              <p className="text-sm text-muted-foreground">
                {testResult.kind === "technical"
                  ? t("settings.alerts.technical.test.success", {
                      count: testResult.alertCount,
                    })
                  : testResult.sent
                    ? t("settings.alerts.test.successWithAlerts", {
                        count: testResult.alertCount,
                      })
                    : t("settings.alerts.test.successNoAlerts")}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </SettingsFormContainer>
  );
}

function RecipientEmailsInput({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: string[];
  onChange: (emails: string[]) => void;
}) {
  const t = useTranslation();
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const addEmails = (input: string) => {
    const candidates = input
      .split(",")
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);

    const valid: string[] = [];
    for (const email of candidates) {
      if (!EMAIL_REGEX.test(email)) {
        setEmailError(t("settings.alerts.recipients.invalidEmail"));
        return;
      }
      if (value.includes(email)) {
        setEmailError(t("settings.alerts.recipients.duplicateEmail"));
        return;
      }
      valid.push(email);
    }

    if (valid.length > 0) {
      onChange([...value, ...valid]);
      setNewEmail("");
      setEmailError(null);
    }
  };

  return (
    <div className="space-y-3">
      <FormLabel>{label}</FormLabel>
      <FormDescription>{description}</FormDescription>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((email) => (
            <Badge
              key={email}
              variant="secondary"
              className="flex items-center gap-1 pl-3 pr-1 py-1.5"
            >
              {email}
              <button
                type="button"
                onClick={() => onChange(value.filter((e) => e !== email))}
                className="ml-1 rounded-full p-0.5 hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="flex gap-2 max-w-md">
        <Input
          type="email"
          placeholder={t("settings.alerts.test.emailPlaceholder")}
          value={newEmail}
          onChange={(e) => {
            setNewEmail(e.target.value);
            setEmailError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (newEmail.trim()) addEmails(newEmail);
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => {
            if (newEmail.trim()) addEmails(newEmail);
          }}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {emailError && <p className="text-sm text-destructive">{emailError}</p>}
    </div>
  );
}
